import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import laptopBanner from '../Assets/banner_mens.png';
import phoneBanner from '../Assets/banner_women.png';
import accessoriesBanner from '../Assets/banner_kids.png';
import { getCategories } from '../../common/api/api';
import { getActiveCategories } from '../../common/categoryTree';

const FALLBACK_CATEGORIES = [
  {
    id: 1,
    label: 'Laptops',
    icon: '💻',
    num: '01',
    subtitle: '500+ Models • All Brands',
    tag: '🏆 Best Seller',
    image: laptopBanner,
    link: '/laptops',
    accent: '#00d4ff',
    gradient: 'linear-gradient(160deg, rgba(0,212,255,0.35) 0%, rgba(6,12,24,0.95) 65%)',
    featured: true,
  },
  {
    id: 2,
    label: 'Smartphones',
    icon: '📱',
    num: '02',
    subtitle: 'Latest 2026 Releases',
    tag: '✨ New In',
    image: phoneBanner,
    link: '/phones',
    accent: '#7c3aed',
    gradient: 'linear-gradient(160deg, rgba(124,58,237,0.35) 0%, rgba(6,12,24,0.95) 65%)',
  },
  {
    id: 3,
    label: 'Accessories',
    icon: '🎧',
    num: '03',
    subtitle: 'Headphones, Watches & More',
    tag: '🔥 Hot Deals',
    image: accessoriesBanner,
    link: '/accessories',
    accent: '#f59e0b',
    gradient: 'linear-gradient(160deg, rgba(245,158,11,0.35) 0%, rgba(6,12,24,0.95) 65%)',
  },
];

const IMAGE_MAP = {
  laptops: laptopBanner,
  mobiles: phoneBanner,
  accessories: accessoriesBanner,
};

const ICON_MAP = {
  laptops: '💻',
  mobiles: '📱',
  accessories: '🎧',
};

const TAGS = ['🏆 Best Seller', '✨ New In', '🔥 Hot Deals', '⚡ Trending'];

const CategoryCard = ({ cat, big }) => (
  <Link to={cat.link} className={`group relative rounded-[22px] overflow-hidden flex items-end border border-white/[0.07] cursor-pointer transition-all duration-[400ms] hover:-translate-y-2 hover:shadow-[0_24px_70px_rgba(0,0,0,0.65)] hover:border-transparent ${big ? 'h-[500px] max-[900px]:h-[380px]' : 'h-[238px] max-[900px]:h-[180px]'}`} style={{ '--accent': cat.accent }}>

    {/* Background image with zoom */}
    <div className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-[500ms] group-hover:scale-[1.07]" style={{ backgroundImage: `url(${cat.image})` }} />

    {/* Color gradient overlay */}
    <div className="absolute inset-0 transition-opacity duration-300" style={{ background: cat.gradient }} />

    {/* Dark bottom fade */}
    <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(6,12,24,0.92) 0%, rgba(6,12,24,0.4) 50%, transparent 100%)' }} />

    {/* Large number watermark */}
    <span className="absolute top-4 right-5 text-[80px] font-black leading-none select-none pointer-events-none transition-all duration-300 group-hover:scale-105" style={{ color: `${cat.accent}18`, letterSpacing: '-4px' }}>
      {cat.num}
    </span>

    {/* Hover glow overlay */}
    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400 pointer-events-none" style={{ background: `radial-gradient(ellipse at 30% 70%, ${cat.accent}22 0%, transparent 65%)` }} />

    {/* Content */}
    <div className="relative z-10 p-6 w-full">
      {/* Tag */}
      <div className="inline-block px-3 py-1 rounded-full text-[10px] font-extrabold tracking-[1.5px] uppercase border mb-2.5" style={{ background: `${cat.accent}20`, color: cat.accent, borderColor: `${cat.accent}50` }}>
        {cat.tag}
      </div>

      <div className={`flex items-end justify-between gap-3`}>
        <div>
          <div className={`block mb-1.5 transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-1 ${big ? 'text-5xl' : 'text-3xl'}`}>{cat.icon}</div>
          <h3 className={`font-black text-white leading-none mb-1 -tracking-[0.5px] ${big ? 'text-[32px] max-[900px]:text-2xl' : 'text-xl'}`}>{cat.label}</h3>
          <p className="text-white/55 text-xs">{cat.subtitle}</p>
        </div>

        {/* Arrow CTA */}
        <div
          className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300"
          style={{ background: `linear-gradient(135deg, ${cat.accent}, ${cat.accent}99)`, boxShadow: `0 4px 20px ${cat.accent}55` }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </div>
      </div>

      {/* Bottom shimmer line */}
      <div className="mt-4 h-[2px] rounded-full overflow-hidden bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="h-full rounded-full transition-all duration-500 w-0 group-hover:w-full" style={{ background: `linear-gradient(90deg, transparent, ${cat.accent}, transparent)` }} />
      </div>
    </div>
  </Link>
);

const CategoryBanners = () => {
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);

  useEffect(() => {
    const loadCategories = () => {
      getCategories()
        .then((data) => {
          const active = getActiveCategories(data);
          if (!active.length) return;
          setCategories(
            active.map((cat, index) => ({
              id: cat._id || index + 1,
              label: cat.name,
              icon: ICON_MAP[cat.slug] || ['🛍️', '📦', '✨', '🚀', '🎯', '🧩'][index % 6],
              num: String(index + 1).padStart(2, '0'),
              subtitle: cat.description || `${(cat.subcategories || []).length} subcategories available`,
              tag: TAGS[index % TAGS.length],
              image: IMAGE_MAP[cat.slug] || [laptopBanner, phoneBanner, accessoriesBanner][index % 3],
              link: `/category/${cat.slug}`,
              accent: cat.accent || FALLBACK_CATEGORIES[index % FALLBACK_CATEGORIES.length].accent,
              gradient: `linear-gradient(160deg, ${(cat.accent || '#00d4ff')}59 0%, rgba(6,12,24,0.95) 65%)`,
              featured: index === 0,
            }))
          );
        })
        .catch(() => {});
    };

    loadCategories();
    window.addEventListener('focus', loadCategories);
    return () => window.removeEventListener('focus', loadCategories);
  }, []);

  const displayCategories = useMemo(() => {
    if (categories.length) return categories;
    return FALLBACK_CATEGORIES;
  }, [categories]);

  const featuredCategory = displayCategories[0];
  const secondaryCategories = displayCategories.slice(1);

  return (
    <section className="py-[90px] px-[8%]">
      <div className="max-w-[1400px] mx-auto">
        {/* Section header */}
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <span className="inline-block bg-[rgba(0,212,255,0.1)] text-[#00d4ff] border border-[rgba(0,212,255,0.25)] px-[18px] py-1.5 rounded-full text-xs font-bold tracking-[2px] uppercase mb-4">🛒 Shop By Category</span>
            <h2 className="text-[44px] font-black text-white -tracking-[1px] leading-none max-[700px]:text-[32px]">
              Browse <span className="shimmer-text">Top Categories</span>
            </h2>
          </div>
          <p className="text-[#4a5568] text-sm max-w-[280px] text-right max-[700px]:text-left">Find exactly what you need — fast, easy, and at the best price.</p>
        </div>

        {/* Asymmetric grid: big left + 2 stacked right */}
        <div className="grid grid-cols-[3fr_2fr] gap-5 max-[900px]:grid-cols-1">
          {/* Big featured card */}
          {featuredCategory && <CategoryCard cat={featuredCategory} big />}

          {/* Dynamic smaller stacked/grid cards */}
          <div className={`grid gap-5 ${secondaryCategories.length > 2 ? 'grid-cols-2 max-[700px]:grid-cols-1' : 'grid-cols-1'}`}>
            {secondaryCategories.map((cat) => (
              <CategoryCard key={cat.id} cat={cat} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CategoryBanners;
